import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import type { QueuedOutboundMessage } from "./useOutboundMessageQueue";

interface OutboundMessageQueuePanelProps<TAttachment> {
  queue: QueuedOutboundMessage<TAttachment>[];
  agentLabel: string;
  onRemove: (id: string) => void;
}

export function OutboundMessageQueuePanel<TAttachment>({
  queue,
  agentLabel,
  onRemove,
}: OutboundMessageQueuePanelProps<TAttachment>) {
  if (queue.length === 0) return null;

  return (
    <div
      className="mx-4 mb-2 rounded-xl border border-slate-700/80 bg-slate-900/60 px-3 py-2"
      data-testid="outbound-message-queue"
      aria-live="polite"
    >
      <p className="mb-2 flex items-center gap-2 text-xs text-slate-400">
        <FontAwesomeIcon icon={faClock} className="size-3 shrink-0" />
        <span>
          Queued — {agentLabel} can&apos;t react to these until the current reply
          finishes. They&apos;ll send automatically, one at a time.
        </span>
      </p>
      <ul className="space-y-1.5">
        {queue.map((item, index) => (
          <li
            key={item.id}
            className="flex items-start gap-2 rounded-lg bg-slate-800/80 px-3 py-2 text-sm text-slate-200"
            data-testid="outbound-message-queue-item"
          >
            <span className="shrink-0 font-mono text-xs text-slate-500">
              {index + 1}.
            </span>
            <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
              {item.content}
            </span>
            <Button
              plain
              className="shrink-0 !p-1"
              aria-label="Remove queued message"
              onClick={() => onRemove(item.id)}
            >
              <FontAwesomeIcon icon={faXmark} className="size-3 text-slate-400" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
